/**
 * A POS-style quantity tile: a square photo (or swatch) the whole of which
 * adds one, with a pill stepper overlaid once the quantity is non-zero, and
 * the name + a caption line beneath. The same shell the cafeteria register
 * uses (`PosItemTile`), without the register's pool-inventory branches, for
 * surfaces that count things by tile — Fresh Backup Meals, supplies.
 */

import { Minus, Plus } from 'lucide-react'
import type { ReactElement, ReactNode } from 'react'

export interface QuantityTileProps {
  name: string
  qty: number
  onAdd: () => void
  onDecrement: () => void
  disabled?: boolean
  /** Hard ceiling; the add controls disable at it. Null = uncapped. */
  max?: number | null
  imageUrl?: string | null
  imageFit?: 'cover' | 'contain'
  /** Second line under the name — a price, a cap, a ship estimate. */
  caption?: ReactNode
}

export function QuantityTile({
  name,
  qty,
  onAdd,
  onDecrement,
  disabled = false,
  max = null,
  imageUrl = null,
  imageFit = 'cover',
  caption,
}: QuantityTileProps): ReactElement {
  const addDisabled = disabled || (typeof max === 'number' && qty >= max)
  return (
    <div className="flex flex-col gap-2" data-testid={`quantity-tile-${name}`}>
      <div className="relative aspect-square overflow-hidden rounded-2xl bg-foreground/[0.04]">
        {/* The whole photo adds one — the biggest possible tap target. */}
        <button
          type="button"
          aria-label={name}
          onClick={onAdd}
          disabled={addDisabled}
          className="absolute inset-0 h-full w-full disabled:cursor-not-allowed disabled:opacity-50">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt=""
              loading="lazy"
              className={imageFit === 'contain' ? 'h-full w-full object-contain' : 'h-full w-full object-cover'}
            />
          ) : null}
        </button>
        <div className="pointer-events-none absolute inset-x-2 bottom-2 flex justify-end">
          {qty > 0 ? (
            <div className="pointer-events-auto flex h-10 w-full items-center justify-between rounded-full bg-background px-1 shadow-md">
              <button
                type="button"
                aria-label={`Remove one ${name}`}
                disabled={disabled}
                onClick={onDecrement}
                className="flex size-8 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50">
                <Minus className="h-4 w-4" />
              </button>
              <span className="text-base font-medium tabular-nums" data-testid="quantity-tile-qty">
                {qty}
              </span>
              <button
                type="button"
                aria-label={`Add one ${name}`}
                disabled={addDisabled}
                onClick={onAdd}
                className="flex size-8 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50">
                <Plus className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              aria-label={`Add one ${name}`}
              disabled={addDisabled}
              onClick={onAdd}
              className="pointer-events-auto flex size-10 items-center justify-center rounded-full bg-background text-foreground shadow-md transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50">
              <Plus className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
      <div className="min-w-0">
        <span className="line-clamp-2 text-sm">{name}</span>
        {caption !== undefined && caption !== null ? (
          <div className="text-sm text-muted-foreground">{caption}</div>
        ) : null}
      </div>
    </div>
  )
}
